import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import makeStyles from "@material-ui/core/styles/makeStyles";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CardMenu from "@saleor/components/CardMenu";
import Container from "@saleor/components/Container";
import FilterBarCustom from "@saleor/components/FilterBarCustom";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import { ProductListColumns } from "@saleor/config";
import { User } from "@saleor/fragments/types/User";
import { sectionNames } from "@saleor/intl";
import ProductKAMSDropdownGroup from "@saleor/products/components/ProductKAMSDropdownGroup";
import { Brand } from "@saleor/products/customTypes/Brand";
import { ProductsUser } from "@saleor/products/customTypes/ProductsUser";
import { TenantDropdownItem } from "@saleor/products/customTypes/TenantDropdownItem";
import {
  GridAttributes_availableInGrid_edges_node,
  GridAttributes_grid_edges_node
} from "@saleor/products/types/GridAttributes";
import { ProductList_products_edges_node } from "@saleor/products/types/ProductList";
import {
  FetchMoreProps,
  FilterPageProps,
  ListActions,
  PageListProps,
  SortPage
} from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProductListUrlDialog, ProductListUrlSortField } from "../../urls";
import ProductList from "../ProductList/ProductList";
import {
  createFilterStructure,
  ProductFilterKeys,
  ProductListFilterOpts
} from "./filters";

export interface SellerBrandFormData {
  viewAs: string;
  sellerBrand: string;
}

export interface ProductListPageProps
  extends PageListProps<ProductListColumns>,
    ListActions,
    FilterPageProps<ProductFilterKeys, ProductListFilterOpts>,
    FetchMoreProps,
    SortPage<ProductListUrlSortField> {
  activeAttributeSortId: string;
  availableInGridAttributes: GridAttributes_availableInGrid_edges_node[];
  brand: string;
  brands: Brand[];
  brandLabel: string;
  currencySymbol: string;
  fetchBrands: (query: string) => void;
  fetchMoreBrands?: FetchMoreProps;
  fetchTenants: (query: string) => void;
  gridAttributes: GridAttributes_grid_edges_node[];
  isAccountManager: boolean;
  isSellerMaster: boolean;
  isStoreAdmin: boolean;
  onChangeType: (typeValue: string) => void;
  onBrandChange: (e) => void;
  onExport: () => void;
  onTenantChange: (e) => void;
  onUpload: (path: ProductListUrlDialog) => void;
  onUserViewChange: (e) => void;
  products: ProductList_products_edges_node[];
  tenant: string;
  tenantLabel: string;
  tenants?: TenantDropdownItem[];
  totalGridAttributes: number;
  userDetail: User;
  userView: string;
  userViews: any;
  userViewLabel: string;
  params: any;
  productsUser: ProductsUser;
  searchBy: (searchBy: string) => void;
  totalCount: number;
  onClearPagination: (boolean) => void;
  handleSelectProducts: (productIds: string[], isChecked: boolean) => void;
  selectedProducts: string[];
}

const useStyles = makeStyles(
  theme => ({
    buttonCreate: {
      margin: theme.spacing(0, 0)
    },
    buttonUpload: {
      margin: theme.spacing(0, 3)
    },
    columnPicker: {
      margin: theme.spacing(0, 3)
    },
    tenantDropdown: {
      margin: theme.spacing(3, 0),
      zIndex: 0
    },
    uploadView: {
      display: "flex",
      flexDirection: "column"
    }
  }),
  { name: "ProductListPage" }
);

export const ProductListPage: React.FC<ProductListPageProps> = props => {
  const {
    availableInGridAttributes,
    brand,
    brands,
    brandLabel,
    currencySymbol,
    currentTab,
    defaultSettings,
    fetchBrands,
    fetchMoreBrands,
    fetchTenants,
    filterOpts,
    gridAttributes,
    hasMore,
    initialSearch,
    isAccountManager,
    isSellerMaster,
    isStoreAdmin,
    loading,
    onAdd,
    onAll,
    onBrandChange,
    onChangeType,
    onExport,
    onFetchMore,
    onFilterChange,
    onSearchChange,
    onTabChange,
    onTabDelete,
    onTabSave,
    onTenantChange,
    onUpdateListSettings,
    onUpload,
    onUserViewChange,
    settings,
    tabs,
    totalCount,
    totalGridAttributes,
    tenant,
    tenantLabel,
    tenants,
    userDetail,
    userView,
    userViews,
    userViewLabel,
    params,
    productsUser,
    searchBy,
    onClearPagination,
    handleSelectProducts,
    selectedProducts,
    ...listProps
  } = props;
  const intl = useIntl();
  const classes = useStyles(props);

  const [anchorUpload, setAnchorUpload] = React.useState(null);
  const openUpload = Boolean(anchorUpload);
  const filterStructure = createFilterStructure(intl, filterOpts, params);

  const isStoreAdminKAMSView =
    isAccountManager && userView === SmopUserTypeCode.STORE_ADMIN;

  const breadcrumbDirectory = [
    {
      name: "Products"
    }
  ];

  return (
    <Container>
      {isAccountManager && (
        <ProductKAMSDropdownGroup
          brand={brand}
          brandChoices={brands}
          brandLabel={brandLabel}
          onBrandChange={onBrandChange}
          userView={userView}
          userViewChoices={userViews}
          userViewLabel={userViewLabel}
          onUserViewChange={onUserViewChange}
        />
      )}
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <PageHeader title={intl.formatMessage(sectionNames.products)}>
        <CardMenu
          menuItems={[
            {
              label: intl.formatMessage({
                defaultMessage: "Export Products",
                description: "export products to csv file, button"
              }),
              onSelect: onExport,
              testId: "export"
            }
          ]}
          data-test="exportProductBtn"
        />
        <>
          <Button
            id="uploadBtn"
            className={classes.buttonUpload}
            onClick={e => setAnchorUpload(e.currentTarget)}
            color="primary"
            variant="contained"
            data-test="add-product"
            startIcon={<CloudUploadIcon />}
          >
            <FormattedMessage defaultMessage="Upload" description="button" />
          </Button>
          {(productsUser.isSellerMaster || productsUser.isSuperUser) && (
            <Button
              id="createProductBtn"
              className={classes.buttonCreate}
              onClick={onAdd}
              color="primary"
              variant="contained"
              data-test="add-product"
            >
              <FormattedMessage
                defaultMessage="Create Product"
                description="button"
              />
            </Button>
          )}
          <Popover
            // id={id}
            open={openUpload}
            anchorEl={anchorUpload}
            onClose={() => setAnchorUpload(null)}
            anchorOrigin={{
              horizontal: "center",
              vertical: "bottom"
            }}
            transformOrigin={{
              horizontal: "center",
              vertical: "top"
            }}
          >
            <Menu
              anchorEl={anchorUpload}
              keepMounted
              open={openUpload}
              onClose={() => setAnchorUpload(null)}
              anchorOrigin={{
                horizontal: "center",
                vertical: "bottom"
              }}
              transformOrigin={{
                horizontal: "center",
                vertical: "top"
              }}
              elevation={0}
              getContentAnchorEl={null}
            >
              {isStoreAdmin ? (
                <MenuItem
                  id="uploadBulkInventoryBtn"
                  onClick={() => {
                    onUpload("uploadBulkInventory");
                    setAnchorUpload(null);
                  }}
                >
                  <FormattedMessage
                    defaultMessage="Upload a Bulk Inventory Update File"
                    description="import products to csv file, button"
                  />
                </MenuItem>
              ) : (
                <>
                  <MenuItem
                    id="uploadBulkPriceBtn"
                    onClick={() => {
                      onUpload("uploadBulkPrice");
                      setAnchorUpload(null);
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="Upload a Bulk Product Price Update File"
                      description="import products to csv file, button"
                    />
                  </MenuItem>
                  <MenuItem
                    id="uploadBulkImageBtn"
                    onClick={() => {
                      onUpload("uploadBulkImage");
                      setAnchorUpload(null);
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="Upload Bulk Product Details File"
                      description="import products to csv file, button"
                    />
                  </MenuItem>
                  <MenuItem
                    id="uploadBulkProductBtn"
                    onClick={() => {
                      onUpload("uploadBulkProduct");
                      setAnchorUpload(null);
                    }}
                  >
                    <FormattedMessage
                      defaultMessage="Upload a Bulk Product File"
                      description="import products to csv file, button"
                    />
                  </MenuItem>
                </>
              )}
            </Menu>
          </Popover>
        </>
      </PageHeader>
      {isStoreAdminKAMSView && (
        <SingleAutocompleteSelectField
          className={classes.tenantDropdown}
          name="tenantDropdown"
          label={intl.formatMessage({
            defaultMessage: "Tenant & Mall"
          })}
          displayValue={tenantLabel}
          choices={tenants}
          value={tenant}
          onChange={onTenantChange}
        />
      )}
      <Card>
        <FilterBarCustom
          currencySymbol={currencySymbol}
          currentTab={currentTab}
          initialSearch={initialSearch}
          onAll={onAll}
          onClearPagination={onClearPagination}
          onFilterChange={onFilterChange}
          onSearchChange={onSearchChange}
          onTabChange={onTabChange}
          onTabDelete={onTabDelete}
          onTabSave={onTabSave}
          tabs={tabs}
          searchBy={searchBy}
          allTabLabel={intl.formatMessage({
            defaultMessage: "All",
            description: "tab name"
          })}
          filterStructure={filterStructure}
          searchPlaceholder={intl.formatMessage({
            defaultMessage: "Type in SKU or Product Name"
          })}
        />
        <ProductList
          totalCount={totalCount}
          {...listProps}
          loading={loading}
          gridAttributes={gridAttributes}
          isAccountManager={isAccountManager}
          isStoreAdmin={isStoreAdmin}
          userView={userView}
          settings={settings}
          onUpdateListSettings={onUpdateListSettings}
          handleSelectProducts={handleSelectProducts}
          selectedProducts={selectedProducts}
        />
      </Card>
    </Container>
  );
};
ProductListPage.displayName = "ProductListPage";
export default ProductListPage;
